import { useEffect, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Card,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../../api/useApi';
import { useAsync } from '../../api/useAsync';
import { PageHeader } from '../../components/PageHeader';
import { UserPicker } from './UserPicker';
import type { ActionResponse, DirectoryUserDto, UserDetailDto } from '../../api/types';

interface Props {
  title: string;
  description: string;
  pageKey: string;
  confirmLabel: string;
  danger?: boolean;
  warning?: ReactNode;
  action: (userId: string, ticketNumber?: string) => Promise<ActionResponse>;
}

export function UserLifecycleActionPage({ title, description, pageKey, confirmLabel, danger, warning, action }: Props) {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === pageKey)?.ticketMode ?? 'None';

  const [selected, setSelected] = useState<DirectoryUserDto | undefined>();
  const [detail, setDetail] = useState<UserDetailDto | undefined>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [ticket, setTicket] = useState('');
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ActionResponse | null>(null);

  useEffect(() => {
    if (!selected) {
      setDetail(undefined);
      return;
    }
    let active = true;
    setDetailLoading(true);
    api
      .getUser(selected.id)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch(() => {
        if (active) setDetail(undefined);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selected, api]);

  function reset() {
    setSelected(undefined);
    setDetail(undefined);
    setTicket('');
    setTicketError(undefined);
    setDone(null);
    setError(null);
  }

  async function confirm() {
    if (!selected) return;
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setTicketError(undefined);
    setBusy(true);
    setError(null);
    try {
      setDone(await action(selected.id, ticket.trim() || undefined));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div>
        <PageHeader title={title} />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
          {done.status === 'PendingApproval' ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
                Diese Aktion ist freigabepflichtig. Den Status findest du unter „Meine Anfragen".
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="success">
              <MessageBarBody>
                <MessageBarTitle>Erledigt</MessageBarTitle>
                {done.message}
              </MessageBarBody>
            </MessageBar>
          )}
          <div>
            <Button appearance="primary" onClick={reset}>
              Weitere Aktion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={title} description={description} />
      <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        <Field label="Benutzer">
          <UserPicker onSelect={setSelected} />
        </Field>

        {detailLoading ? <Spinner size="tiny" label="Lade Details…" /> : null}

        {detail ? (
          <Card
            style={{
              padding: tokens.spacingVerticalL,
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacingVerticalS,
            }}
          >
            <Text weight="semibold" className="pii">
              {detail.displayName}
            </Text>
            <Caption1 className="pii">{detail.userPrincipalName}</Caption1>
            <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' }}>
              <Caption1>Status:</Caption1>
              <Badge appearance="tint" color={detail.accountEnabled ? 'success' : 'danger'}>
                {detail.accountEnabled ? 'aktiv' : 'gesperrt'}
              </Badge>
              {detail.jobTitle ? <Caption1>· {detail.jobTitle}</Caption1> : null}
            </div>
            {warning ? (
              <MessageBar intent="warning">
                <MessageBarBody>{warning}</MessageBarBody>
              </MessageBar>
            ) : null}
          </Card>
        ) : null}

        {ticketMode !== 'None' ? (
          <Field
            label="Ticketnummer"
            required={ticketMode === 'Required'}
            validationMessage={ticketError}
            hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
          >
            <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. INC0012345" />
          </Field>
        ) : null}

        <div>
          <Button
            appearance="primary"
            style={danger ? { backgroundColor: tokens.colorPaletteRedBackground3 } : undefined}
            disabled={!detail || busy}
            onClick={confirm}
          >
            {busy ? 'Bitte warten…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
